/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { DatePicker, Button, Typography, message, Skeleton } from "antd";


const { RangePicker } = DatePicker;
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { formatIndianNumber, formatDeliveryPercentage } from "../../../../utils";
import { DirectorDashboardService3 } from "../../../../services/api/directorDashboardService3";
import { getBookingFieldValue } from "../bookingSummaryMapping";
import axios from "axios";
import "./style.scss";

import "../style.scss";

const { Title } = Typography;

const DEFAULT_ZONES = ["EAST", "NORTH", "SOUTH1", "SOUTH2", "WEST1", "WEST2"];

const CATEGORY_BUSINESS_DATA = "Business Data";
const CATEGORY_SHIPMENT_COUNTS = "Shipment Counts";

const isBookingReportCategory = (category) =>
    category === CATEGORY_BUSINESS_DATA || category === CATEGORY_SHIPMENT_COUNTS;

const transformBookingZoneWiseApiData = (dataArray, category) => {
    const transformed = category === "Business Data"
        ? { cash: {}, account: {}, dp: {}, pieceRate: {} }
        : { cashCount: {}, accountCount: {}, dpCounts: {}, pieceRateCount: {} };
    const zoneList = [];

    (dataArray || []).forEach((item) => {
        const zone = item.zone || "Unknown";
        zoneList.push(zone);

        if (category === CATEGORY_BUSINESS_DATA) {
            transformed.cash[zone] = getBookingFieldValue(item, "Retail", CATEGORY_BUSINESS_DATA);
            transformed.account[zone] = getBookingFieldValue(item, "Credit", CATEGORY_BUSINESS_DATA);
            transformed.dp[zone] = getBookingFieldValue(item, "DP Client", CATEGORY_BUSINESS_DATA);
            transformed.pieceRate[zone] = getBookingFieldValue(item, "Piece Rate", CATEGORY_BUSINESS_DATA);
        } else {
            transformed.cashCount[zone] = getBookingFieldValue(item, "Retail", CATEGORY_SHIPMENT_COUNTS);
            transformed.accountCount[zone] = getBookingFieldValue(item, "Credit", CATEGORY_SHIPMENT_COUNTS);
            transformed.dpCounts[zone] = getBookingFieldValue(item, "DP Client", CATEGORY_SHIPMENT_COUNTS);
            transformed.pieceRateCount[zone] = getBookingFieldValue(item, "Piece Rate", CATEGORY_SHIPMENT_COUNTS);
        }
    });

    return { transformed, zoneList };
};

const transformDeliveryZoneWiseApiData = (dataArray) => {
    const transformed = {
        deliveryPercentage: {},
        delivered: {},
        pending: {},
        returnInitiated: {},
        rtoDelivered: {},
        undelivered: {},
        rtoUndelivered: {},
        total: {},
        totalDrs: {},
    };
    const zoneList = [];

    (dataArray || []).forEach((item) => {
        const zone = item.zone || "Unknown";
        zoneList.push(zone);
        transformed.deliveryPercentage[zone] = parseFloat(item.delivery_percentage) || 0;
        transformed.delivered[zone] = item.delivered || 0;
        transformed.pending[zone] = item.pending || 0;
        transformed.returnInitiated[zone] = item.return_initiated || 0;
        transformed.rtoDelivered[zone] = item.rto_delivered || 0;
        transformed.undelivered[zone] = item.undelivered || 0;
        transformed.rtoUndelivered[zone] = item.rto_undelivered || 0;
        transformed.total[zone] = item.total_shipments || 0;
        transformed.totalDrs[zone] = item.total_drs || 0;
    });

    return { transformed, zoneList };
};

const ZoneWiseView3 = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedDate, setSelectedDate] = useState(moment());
    const [selectedDateRange, setSelectedDateRange] = useState([moment().startOf("month"), moment()]);
    const [loading, setLoading] = useState(false);
    const [zoneWiseData, setZoneWiseData] = useState({});
    const [reportCategory, setReportCategory] = useState("Business Data"); // Business Data, Shipment Counts, Delivery Data
    const [dataType, setDataType] = useState("today"); // "today" or "range"
    const hasLoadedRef = useRef(false);
    const isInitializingRef = useRef(true);

    const [displayZones, setDisplayZones] = useState(DEFAULT_ZONES);

    useEffect(() => {
        const locState = location.state || {};
        // Get report category, type, and data type from location state if available
        const category = locState.reportCategory || "Business Data";
        const typeMode = locState.type || "today";
        const businessType = locState.businessType || null;

        // Set flag to indicate we're initializing
        isInitializingRef.current = true;

        setReportCategory(category);
        setDataType(typeMode);

        if (locState.date) {
            const start = moment(locState.date, "YYYY-MM-DD", true);
            if (start.isValid()) {
                setSelectedDate(start);
                const end = locState.endDate
                    ? moment(locState.endDate, "YYYY-MM-DD", true)
                    : start;
                setSelectedDateRange([start, end.isValid() ? end : start]);
            }
        }

        // If businessType is provided, navigate to ZoneList route instead
        if (businessType) {
            navigate("/zone-list2", {
                state: {
                    type: typeMode,
                    reportType: locState.reportType || "Piece Rate",
                    reportCategory: category,
                    date: locState.date || moment().format("YYYY-MM-DD"),
                    businessType: businessType,
                },
            });
            return;
        }

        // Set date from location.state (this is the date from API response)
        if (typeMode === "today") {
            // For "today" type, set single date
            if (locState.date) {
                // Parse the date from location.state (format: "YYYY-MM-DD")
                const dateValue = moment(locState.date, "YYYY-MM-DD", true);

                // Only set if valid, otherwise use default
                if (dateValue.isValid()) {
                    setSelectedDate(dateValue);
                } else {
                    // Fallback: try parsing without strict mode
                    const fallbackDate = moment(locState.date);
                    setSelectedDate(fallbackDate.isValid() ? fallbackDate : moment());
                }
            } else {
                // If no date in state, use default (today)
                setSelectedDate(moment());
            }
        } else {
            // For "range" type, set date range
            if (locState.date && locState.endDate) {
                // Parse dates from location.state (format: "YYYY-MM-DD")
                const startDate = moment(locState.date, "YYYY-MM-DD", true);
                const endDate = moment(locState.endDate, "YYYY-MM-DD", true);

                if (startDate.isValid() && endDate.isValid()) {
                    setSelectedDateRange([startDate, endDate]);
                } else {
                    // Fallback: use month start to today
                    setSelectedDateRange([moment().startOf("month"), moment()]);
                }
            } else if (locState.date) {
                // Only start date available, use it as both start and end
                const dateValue = moment(locState.date, "YYYY-MM-DD", true);
                if (dateValue.isValid()) {
                    setSelectedDateRange([dateValue, dateValue]);
                } else {
                    setSelectedDateRange([moment().startOf("month"), moment()]);
                }
            } else {
                // If no date in state, use default (month start to today)
                setSelectedDateRange([moment().startOf("month"), moment()]);
            }
        }

        // Mark initialization as complete after state updates
        // Use requestAnimationFrame to ensure state updates are processed
        requestAnimationFrame(() => {
            setTimeout(() => {
                isInitializingRef.current = false;
            }, 0);
        });
    }, [location.state, navigate]);

    // Transform API response to component format
    const transformApiData = (apiData, dateKey, category) => {
        const dateData = apiData?.data?.[dateKey] || {};
        const transformed = {};

        if (category === "Business Data") {
            // Transform business data
            transformed.cash = {};
            transformed.account = {};
            transformed.dp = {};
            transformed.pieceRate = {};

            DEFAULT_ZONES.forEach((zone) => {
                const zoneData = dateData[zone]?.business || {};
                transformed.cash[zone] = zoneData.Cash || 0;
                transformed.account[zone] = zoneData.Account || 0;
                transformed.dp[zone] = zoneData.DP || 0;
                transformed.pieceRate[zone] = zoneData.Piece || 0;
            });
        } else if (category === "Shipment Counts") {
            // Transform shipment count data
            transformed.cashCount = {};
            transformed.accountCount = {};
            transformed.dpCounts = {};
            transformed.pieceRateCount = {};

            DEFAULT_ZONES.forEach((zone) => {
                const zoneData = dateData[zone]?.shipment_count || {};
                transformed.cashCount[zone] = zoneData.Cash || 0;
                transformed.accountCount[zone] = zoneData.Account || 0;
                transformed.dpCounts[zone] = zoneData.DP || 0;
                transformed.pieceRateCount[zone] = zoneData.Piece || 0;
            });
        }

        return transformed;
    };

    useEffect(() => {
        // Load data when reportCategory/dataType/selectedDate or selectedDateRange changes
        const isDeliveryData = reportCategory === "Delivery Data";
        const isTodayMode = dataType === "today";
        const isRangeMode = dataType === "range";
        
        const hasValidDateRange = Boolean(selectedDateRange?.[0] && selectedDateRange?.[1]);
        const isValidDelivery = isDeliveryData && hasValidDateRange;
        const isValidToday = !isDeliveryData && isTodayMode && selectedDate;
        const isValidRange = !isDeliveryData && isRangeMode && hasValidDateRange;
        
        if (!(isValidDelivery || isValidToday || isValidRange)) {
            return;
        }

        const loadKey = reportCategory === "Delivery Data"
            ? `${reportCategory}-${selectedDateRange[0].format("YYYY-MM-DD")}-${selectedDateRange[1].format("YYYY-MM-DD")}`
            : dataType === "today"
                ? `${reportCategory}-${dataType}-${selectedDate.format("YYYY-MM-DD")}`
                : `${reportCategory}-${dataType}-${selectedDateRange[0].format("YYYY-MM-DD")}-${selectedDateRange[1].format("YYYY-MM-DD")}`;

        const controller = new AbortController();

        // Define the fetch function
        const fetchZoneWiseData = async () => {
            setLoading(true);

            try {
                // Use date from selectedDate or selectedDateRange based on dataType
                let fromDate, toDate;

                if (dataType === "today") {
                    // For "today" type, use the selectedDate (from DatePicker or location.state)
                    fromDate = selectedDate.format("YYYY-MM-DD");
                    toDate = selectedDate.format("YYYY-MM-DD");
                } else {
                    // For range, use selectedDateRange
                    if (selectedDateRange && selectedDateRange.length === 2 && selectedDateRange[0] && selectedDateRange[1]) {
                        fromDate = selectedDateRange[0].format("YYYY-MM-DD");
                        toDate = selectedDateRange[1].format("YYYY-MM-DD");
                    } else {
                        // Fallback: use location.state dates or defaults
                        fromDate = location.state?.date || moment().startOf("month").format("YYYY-MM-DD");
                        toDate = location.state?.endDate || location.state?.date || moment().format("YYYY-MM-DD");
                    }
                }

                if (reportCategory === "Delivery Data") {
                    // Date Preparation
                    const rangeFrom = selectedDateRange?.[0] || moment().subtract(1, "day");
                    const rangeTo = selectedDateRange?.[1] || rangeFrom;
                    const deliveryFromDate = rangeFrom.format("YYYY-MM-DD");
                    const deliveryToDate = rangeTo.format("YYYY-MM-DD");
                    
                    // API Call
                    const response = await DirectorDashboardService3.getDeliveryZoneWiseBreakdown(deliveryFromDate, deliveryToDate);
                    
                    // Response Handling
                    if (response?.data?.data?.length) {
                        const { transformed, zoneList } = transformDeliveryZoneWiseApiData(response.data.data);
                        setZoneWiseData(transformed);
                        setDisplayZones(zoneList);
                    } else {
                        setZoneWiseData({});
                        setDisplayZones([]);
                        message.warning("No data available for the selected date range");
                    }
                    return;
                }

                if (isBookingReportCategory(reportCategory)) {
                    const response = await DirectorDashboardService3.getBookingZoneWiseBreakdown(fromDate, toDate);
                    if (response?.data?.data?.length) {
                        const { transformed, zoneList } = transformBookingZoneWiseApiData(response.data.data, reportCategory);
                        setZoneWiseData(transformed);
                        setDisplayZones(zoneList);
                    } else {
                        setZoneWiseData({});
                        setDisplayZones([]);
                        message.warning("No data available for the selected date range");
                    }
                    return;
                }

                setDisplayZones(DEFAULT_ZONES);
                const response = await DirectorDashboardService3.getZoneWiseData(fromDate, toDate);

                if (response?.data) {
                    // For date range, we might need to aggregate data across multiple dates
                    let dateKey = toDate;

                    // If range and multiple dates exist, aggregate them
                    if (dataType === "range" && response.data.data) {
                        const dateKeys = Object.keys(response.data.data);
                        if (dateKeys.length > 0) {
                            // Aggregate data across all dates
                            const aggregated = {};
                            dateKeys.forEach((key) => {
                                const dateData = response.data.data[key];
                                DEFAULT_ZONES.forEach((zone) => {
                                    if (!aggregated[zone]) {
                                        aggregated[zone] = {
                                            business: {},
                                            shipment_count: {},
                                            drs: {},
                                        };
                                    }

                                    // Aggregate business data
                                    Object.keys(dateData[zone]?.business || {}).forEach((metric) => {
                                        aggregated[zone].business[metric] =
                                            (aggregated[zone].business[metric] || 0) +
                                            (dateData[zone]?.business?.[metric] || 0);
                                    });

                                    // Aggregate shipment_count data
                                    Object.keys(dateData[zone]?.shipment_count || {}).forEach((metric) => {
                                        aggregated[zone].shipment_count[metric] =
                                            (aggregated[zone].shipment_count[metric] || 0) +
                                            (dateData[zone]?.shipment_count?.[metric] || 0);
                                    });

                                    // Aggregate drs data
                                    Object.keys(dateData[zone]?.drs || {}).forEach((metric) => {
                                        aggregated[zone].drs[metric] =
                                            (aggregated[zone].drs[metric] || 0) +
                                            (dateData[zone]?.drs?.[metric] || 0);
                                    });
                                });
                            });

                            // Create a synthetic date key for aggregated data
                            const syntheticData = {
                                data: {
                                    [toDate]: aggregated,
                                },
                            };
                            const transformed = transformApiData(syntheticData, toDate, reportCategory);
                            setZoneWiseData(transformed);
                            setLoading(false);
                            return;
                        }
                    }

                    // Transform single date data
                    const transformed = transformApiData(response.data, dateKey, reportCategory);
                    setZoneWiseData(transformed);
                } else {
                    setZoneWiseData({});
                    message.warning("No data available for the selected date range");
                }
            } catch (error) {
                if (axios.isCancel(error)) return;
                console.error("Error loading zone wise data:", error);
                message.error(error?.message || "Failed to load zone wise data");
                setZoneWiseData({});
            } finally {
                setLoading(false);
            }
        };

        // Skip if still initializing (to prevent calls during initial state setup)
        if (isInitializingRef.current) {
            // Wait a bit for initialization to complete, then call API
            const timer = setTimeout(() => {
                if (!isInitializingRef.current && (!hasLoadedRef.current || hasLoadedRef.current !== loadKey)) {
                    hasLoadedRef.current = loadKey;
                    fetchZoneWiseData();
                }
            }, 150);
            return () => clearTimeout(timer);
        }

        // Only call if this is a new combination
        if (!hasLoadedRef.current || hasLoadedRef.current !== loadKey) {
            hasLoadedRef.current = loadKey;
            fetchZoneWiseData();
        }

        return () => {
            controller.abort();
            // clear timer if exists (though timer variable is local to closure above)
        };
    }, [reportCategory, dataType, selectedDate, selectedDateRange]);


    const handleDateSubmit = () => {
        // Reset the ref to allow manual reload with new date/range
        hasLoadedRef.current = null;

        // Create a unique key for the current selection
        const loadKey = reportCategory === "Delivery Data"
            ? `${reportCategory}-${selectedDateRange[0].format("YYYY-MM-DD")}-${selectedDateRange[1].format("YYYY-MM-DD")}`
            : dataType === "today"
                ? `${reportCategory}-${dataType}-${selectedDate.format("YYYY-MM-DD")}`
                : `${reportCategory}-${dataType}-${selectedDateRange[0].format("YYYY-MM-DD")}-${selectedDateRange[1].format("YYYY-MM-DD")}`;

        // Call the API directly
        const fetchZoneWiseData = async () => {
            setLoading(true);

            try {
                // Use date from selectedDate or selectedDateRange based on dataType
                let fromDate, toDate;

                if (dataType === "today") {
                    fromDate = selectedDate.format("YYYY-MM-DD");
                    toDate = selectedDate.format("YYYY-MM-DD");
                } else {
                    if (selectedDateRange && selectedDateRange.length === 2 && selectedDateRange[0] && selectedDateRange[1]) {
                        fromDate = selectedDateRange[0].format("YYYY-MM-DD");
                        toDate = selectedDateRange[1].format("YYYY-MM-DD");
                    } else {
                        fromDate = location.state?.date || moment().startOf("month").format("YYYY-MM-DD");
                        toDate = location.state?.endDate || location.state?.date || moment().format("YYYY-MM-DD");
                    }
                }

                if (reportCategory === "Delivery Data") {
                    // Date Preparation
                    const rangeFrom = selectedDateRange?.[0] || moment().subtract(1, "day");
                    const rangeTo = selectedDateRange?.[1] || rangeFrom;
                    const deliveryFromDate = rangeFrom.format("YYYY-MM-DD");
                    const deliveryToDate = rangeTo.format("YYYY-MM-DD");
                    
                    // API Call
                    const response = await DirectorDashboardService3.getDeliveryZoneWiseBreakdown(deliveryFromDate, deliveryToDate);
                    
                    // Response Handling
                    if (response?.data?.data?.length) {
                        const { transformed, zoneList } = transformDeliveryZoneWiseApiData(response.data.data);
                        setZoneWiseData(transformed);
                        setDisplayZones(zoneList);
                    } else {
                        setZoneWiseData({});
                        setDisplayZones([]);
                        message.warning("No data available for the selected date range");
                    }
                    return;
                }

                if (isBookingReportCategory(reportCategory)) {
                    const response = await DirectorDashboardService3.getBookingZoneWiseBreakdown(fromDate, toDate);
                    if (response?.data?.data?.length) {
                        const { transformed, zoneList } = transformBookingZoneWiseApiData(response.data.data, reportCategory);
                        setZoneWiseData(transformed);
                        setDisplayZones(zoneList);
                    } else {
                        setZoneWiseData({});
                        setDisplayZones([]);
                        message.warning("No data available for the selected date range");
                    }
                    return;
                }

                setDisplayZones(DEFAULT_ZONES);
                const response = await DirectorDashboardService3.getZoneWiseData(fromDate, toDate);

                if (response?.data) {
                    let dateKey = toDate;

                    // If range and multiple dates exist, aggregate them
                    if (dataType === "range" && response.data.data) {
                        const dateKeys = Object.keys(response.data.data);
                        if (dateKeys.length > 0) {
                            const aggregated = {};
                            dateKeys.forEach((key) => {
                                const dateData = response.data.data[key];
                                DEFAULT_ZONES.forEach((zone) => {
                                    if (!aggregated[zone]) {
                                        aggregated[zone] = {
                                            business: {},
                                            shipment_count: {},
                                            drs: {},
                                        };
                                    }

                                    Object.keys(dateData[zone]?.business || {}).forEach((metric) => {
                                        aggregated[zone].business[metric] =
                                            (aggregated[zone].business[metric] || 0) +
                                            (dateData[zone]?.business?.[metric] || 0);
                                    });

                                    Object.keys(dateData[zone]?.shipment_count || {}).forEach((metric) => {
                                        aggregated[zone].shipment_count[metric] =
                                            (aggregated[zone].shipment_count[metric] || 0) +
                                            (dateData[zone]?.shipment_count?.[metric] || 0);
                                    });

                                    Object.keys(dateData[zone]?.drs || {}).forEach((metric) => {
                                        aggregated[zone].drs[metric] =
                                            (aggregated[zone].drs[metric] || 0) +
                                            (dateData[zone]?.drs?.[metric] || 0);
                                    });
                                });
                            });

                            const syntheticData = {
                                data: {
                                    [toDate]: aggregated,
                                },
                            };
                            const transformed = transformApiData(syntheticData, toDate, reportCategory);
                            setZoneWiseData(transformed);
                            setLoading(false);
                            return;
                        }
                    }

                    const transformed = transformApiData(response.data, dateKey, reportCategory);
                    setZoneWiseData(transformed);
                } else {
                    setZoneWiseData({});
                    message.warning("No data available for the selected date range");
                }
            } catch (error) {
                console.error("Error loading zone wise data:", error);
                message.error(error?.message || "Failed to load zone wise data");
                setZoneWiseData({});
            } finally {
                setLoading(false);
            }
        };

        hasLoadedRef.current = loadKey;
        fetchZoneWiseData();
    };

    // Use date from location.state for display (this is the date from API response)
    const getDisplayDate = () => {
        if (selectedDateRange?.[0] && selectedDateRange?.[1] && (reportCategory === "Delivery Data" || dataType === "range")) {
            const fromStr = selectedDateRange[0].format("DD/MM/YYYY");
            const toStr = selectedDateRange[1].format("DD/MM/YYYY");
            return fromStr === toStr ? fromStr : `${fromStr} - ${toStr}`;
        }
        if (dataType === "today") {
            // For "today" type, use the date from location.state
            if (location.state?.date) {
                const dateMoment = moment(location.state.date, "YYYY-MM-DD", true);
                return dateMoment.isValid() ? dateMoment.format("DD/MM/YYYY") : selectedDate.format("DD/MM/YYYY");
            }
            return selectedDate.format("DD/MM/YYYY");
        } else {
            // For range, use dates from location.state
            if (location.state?.date && location.state?.endDate) {
                const fromDateMoment = moment(location.state.date, "YYYY-MM-DD", true);
                const toDateMoment = moment(location.state.endDate, "YYYY-MM-DD", true);
                if (fromDateMoment.isValid() && toDateMoment.isValid()) {
                    return `${fromDateMoment.format("DD/MM/YYYY")} - ${toDateMoment.format("DD/MM/YYYY")}`;
                }
            }
            // Fallback to selectedDate
            const rangeStartDate = selectedDate.startOf("month").format("DD/MM/YYYY");
            const rangeEndDate = selectedDate.format("DD/MM/YYYY");
            return `${rangeStartDate} - ${rangeEndDate}`;
        }
    };
    const displayDate = getDisplayDate();

    const handleGoBack = () => {
        navigate("/director-dashboardNew2");
    };

    // Get row types based on report category
    const getRowTypes = () => {
        if (reportCategory === "Business Data") {
            return [
                { key: "cash", label: "Retail", dataKey: "cash" },
                { key: "account", label: "Credit", dataKey: "account" },
                { key: "dp", label: "DP Client", dataKey: "dp" },
                { key: "pieceRate", label: "Piece Rate", dataKey: "pieceRate" },
            ];
        } else if (reportCategory === "Shipment Counts") {
            return [
                { key: "cashCount", label: "Retail", dataKey: "cashCount" },
                { key: "accountCount", label: "Credit", dataKey: "accountCount" },
                { key: "dpCounts", label: "DP Client", dataKey: "dpCounts" },
                { key: "pieceRateCount", label: "Piece Rate", dataKey: "pieceRateCount" },
            ];
        } else if (reportCategory === "Delivery Data") {
            return [
                { key: "deliveryPercentage", label: "Delivery %", dataKey: "deliveryPercentage" },
                { key: "delivered", label: "Delivered", dataKey: "delivered" },
                { key: "pending", label: "Pending", dataKey: "pending" },
                { key: "returnInitiated", label: "Return Initiated", dataKey: "returnInitiated" },
                { key: "rtoDelivered", label: "RTO Delivered", dataKey: "rtoDelivered" },
                { key: "undelivered", label: "Undelivered", dataKey: "undelivered" },
                { key: "rtoUndelivered", label: "RTO Undelivered", dataKey: "rtoUndelivered" },
                { key: "total", label: "Total", dataKey: "total" },
                { key: "totalDrs", label: "Total DRS", dataKey: "totalDrs" },
            ];
        }
        return [];
    };

    // Calculate totals for each row and column
    const calculateRowTotal = (dataKey) => {
        const data = zoneWiseData[dataKey] || {};
        return displayZones.reduce((sum, zone) => sum + (data[zone] || 0), 0);
    };

    const calculateColumnTotal = (zone) => {
        if (reportCategory === "Delivery Data") {
            return zoneWiseData.total?.[zone] || 0;
        }
        const rowTypes = getRowTypes().filter((type) => type.dataKey !== "totalDrs");
        return rowTypes.reduce((sum, type) => {
            const data = zoneWiseData[type.dataKey] || {};
            return sum + (data[zone] || 0);
        }, 0);
    };

    const calculateGrandTotal = () => {
        if (reportCategory === "Delivery Data") {
            return calculateRowTotal("total");
        }
        return displayZones.reduce((sum, zone) => sum + calculateColumnTotal(zone), 0);
    };

    const getZoneTotalValue = (zone) => {
        if (reportCategory === "Delivery Data") {
            return zoneWiseData.total?.[zone] || 0;
        }
        const rowTypes = getRowTypes();
        return rowTypes.reduce((sum, type) => {
            const data = zoneWiseData[type.dataKey] || {};
            return sum + (data[zone] || 0);
        }, 0);
    };

    const getMetricTotalValue = (metricKey) => {
        if (reportCategory === "Delivery Data" && metricKey === "deliveryPercentage") {
            const totalShipments = calculateRowTotal("total");
            const delivered = calculateRowTotal("delivered");
            if (!totalShipments) return 0;
            return parseFloat(((delivered / totalShipments) * 100).toFixed(2));
        }
        return calculateRowTotal(metricKey);
    };

    return (
        <div className="director-dashboard zone-wise-page">
            {/* Header Section */}
            <div className="zone-wise-header">
                <div className="header-left">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleGoBack}
                        className="back-button"
                    />
                    <Title level={4} className="page-title">
                        Zone Wise {reportCategory} - {displayDate}
                    </Title>
                </div>

                <div className="header-right">
                    {/* Date Filter moved here */}
                    <div className="zone-wise-date-filter">
                        {reportCategory === "Delivery Data" || dataType === "range" ? (
                            <RangePicker
                                format="DD/MM/YYYY"
                                value={selectedDateRange}
                                onChange={(dates) => {
                                    if (dates && dates[0] && dates[1]) {
                                        setSelectedDateRange([dates[0], dates[1]]);
                                    } else {
                                        const yesterday = moment().subtract(1, "day");
                                        setSelectedDateRange([yesterday, yesterday]);
                                    }
                                }}
                                className="zone-wise-date-picker hub-ops-range-picker"
                                allowClear={false}
                                disabledDate={(current) => current && current >= moment().startOf("day")}
                            />
                        ) : (
                            <DatePicker
                                format="DD/MM/YYYY"
                                value={selectedDate}
                                onChange={(date) => setSelectedDate(date || moment())}
                                className="zone-wise-date-picker"
                                allowClear={false}
                            />
                        )}
                        <Button type="primary" onClick={handleDateSubmit} className="zone-wise-submit-btn">
                            Submit
                        </Button>
                    </div>
                </div>
            </div>

            {/* Zone Cards Grid */}
            <div className="zone-cards-grid">
                {loading ? (
                    // Render 6 Skeleton cards to match the zones length
                    Array.from({ length: Math.max(displayZones.length, 1) }).map((_, index) => (
                        <div className="zone-data-card" key={`skeleton-${index}`}>
                            <div className="card-left-section">
                                <Skeleton.Input active size="small" style={{ width: 60, marginBottom: 8 }} />
                                <Skeleton.Input active size="large" style={{ width: 100 }} />
                            </div>

                            <div className="card-divider"></div>

                            <div className="card-right-section">
                                <div className="metrics-grid">
                                    {Array.from({ length: 4 }).map((_, i) => (
                                        <div className="metric-item" key={i}>
                                            <Skeleton.Input active size="small" style={{ width: 40, marginBottom: 4 }} />
                                            <Skeleton.Input active size="default" style={{ width: 60 }} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    displayZones.map((zone) => {

                        const zoneTotal = getZoneTotalValue(zone);
                        const rowTypes = getRowTypes();

                        return (
                            <div className="zone-data-card" key={zone}>
                                <div className="card-left-section">
                                    <div className="zone-name">{zone}</div>
                                    <div className="zone-total-value">
                                        {reportCategory === "Business Data" ? "₹" : ""}
                                        {formatIndianNumber(zoneTotal)}
                                        {reportCategory === "Business Data" && ".00"}
                                    </div>
                                </div>

                                <div className="card-divider"></div>

                                <div className="card-right-section">
                                    <div className="metrics-grid">
                                        {rowTypes.map((type) => {
                                            const val = zoneWiseData[type.dataKey]?.[zone] || 0;
                                            const metricDisplay = reportCategory === "Delivery Data" && type.dataKey === "deliveryPercentage"
                                                ? formatDeliveryPercentage(val)
                                                : `${reportCategory === "Business Data" ? "₹ " : ""}${formatIndianNumber(val)}${reportCategory === "Business Data" ? ".00" : ""}`;
                                            return (
                                                <div className="metric-item" key={type.key}>
                                                    <div className="metric-label">{type.label}</div>
                                                    <div className="metric-value">{metricDisplay}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Footer Totals */}
            {!loading && (
                <div className="zone-wise-footer">
                    <div className="footer-metrics">
                        {getRowTypes().map((type) => {
                            const totalVal = getMetricTotalValue(type.dataKey);
                            const footerDisplay = reportCategory === "Delivery Data" && type.dataKey === "deliveryPercentage"
                                ? formatDeliveryPercentage(totalVal)
                                : `${reportCategory === "Business Data" ? "₹ " : ""}${formatIndianNumber(totalVal)}${reportCategory === "Business Data" ? ".00" : ""}`;
                            return (
                                <div className="footer-metric-item" key={type.key}>
                                    <div className="f-metric-label">{type.label}</div>
                                    <div className="f-metric-value">{footerDisplay}</div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="footer-grand-total">
                        <span className="grand-total-label">
                            {reportCategory === "Business Data"
                                ? "Total Amount :"
                                : reportCategory === "Delivery Data"
                                    ? "Total Shipments :"
                                    : "Total Count :"}
                        </span>
                        <span className="grand-total-value">

                            {reportCategory === "Business Data" ? "₹" : ""}
                            {formatIndianNumber(calculateGrandTotal())}
                            {reportCategory === "Business Data" && ".00"}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ZoneWiseView3;

